import React, { FC, ReactElement, useState } from 'react'
import * as yup from 'yup'
import { Formik } from 'formik'
import { Form, Row, Col, Alert } from 'react-bootstrap'
import Auth from '@aws-amplify/auth'
import LoadingButton from '../../LoadingButton'

interface Props {
  setSignupSuccess: (signupSuccess: boolean) => void
  email: string
  setLoginStage: (loginStage: 'reset' | 'signup') => void
}

const schema = yup.object({
  newPassword: yup
    .string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters'),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('newPassword')], 'Passwords must match')
    .required('Passwords must match'),
  terms: yup.bool().oneOf([true], 'Terms must be agreed upon'),
})

const SignupForm: FC<Props> = ({ setSignupSuccess, email, setLoginStage }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const signup = async (values: {
    newPassword: string
    confirmPassword: string
    terms: boolean
  }): Promise<void> => {
    setIsLoading(true)

    try {
      await Auth.signUp({
        username: email,
        password: values.newPassword,
      })

      setLoginStage('signup')
      setSignupSuccess(true)
    } catch (e) {
      setErrorMessage(e.message)
      setIsLoading(false)
    }
  }

  return (
    <>
      {errorMessage && (
        <Alert variant="danger">
          <Alert.Heading>Error</Alert.Heading>
          <span>{errorMessage}</span>
        </Alert>
      )}
      <Formik
        validationSchema={schema}
        onSubmit={signup}
        initialValues={{
          newPassword: '',
          confirmPassword: '',
          terms: false,
        }}
      >
        {({
          handleSubmit,
          values,
          handleChange,
          touched,
          errors,
          submitCount,
          setFieldValue,
        }): ReactElement => (
          <Form noValidate onSubmit={handleSubmit}>
            <Form.Group controlId="email">
              <Form.Label>Email</Form.Label>
              <Form.Control disabled readOnly required value={email} />
            </Form.Group>
            <div className="password-requirements">
              <Row className="title">
                <Col>
                  <h5>Password Requirements</h5>
                </Col>
              </Row>
              <Row className="requirements">
                <Col>
                  <ul>
                    <li>Minimum of 8 characters</li>
                    <li>At least 1 number</li>
                    <li>At least 1 special character</li>
                    <li>Uppercase letters</li>
                    <li>Lowercase letters</li>
                  </ul>
                </Col>
              </Row>
            </div>
            <Form.Group controlId="newPassword">
              <Form.Label>New Password</Form.Label>
              <Form.Control
                type="password"
                required
                value={values.newPassword}
                onChange={handleChange}
                isValid={touched.newPassword && !errors.newPassword}
                isInvalid={submitCount > 0 && !!errors.newPassword}
              />
              <Form.Control.Feedback type="invalid">
                <Alert variant="danger">{errors.newPassword}</Alert>
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group controlId="confirmPassword">
              <Form.Label>Confirm Password</Form.Label>
              <Form.Control
                type="password"
                required
                value={values.confirmPassword}
                onChange={handleChange}
                isValid={touched.confirmPassword && !errors.confirmPassword}
                isInvalid={submitCount > 0 && !!errors.confirmPassword}
              />
              <Form.Control.Feedback type="invalid">
                <Alert variant="danger">{errors.confirmPassword}</Alert>
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group controlId="terms" className="terms">
              <Form.Check
                required
                label={
                  <p>
                    I agree to Maverick&apos;s
                    <a href="https://maverickapp.io/msa" rel="noopener noreferrer" target="_blank">
                      {' '}
                      Terms and Conditions
                    </a>
                  </p>
                }
                feedback={errors.terms && <Alert variant="danger">{errors.terms}</Alert>}
                // isValid={touched.terms && !errors.terms}
                isInvalid={submitCount > 0 && !!errors.terms}
                checked={values.terms}
                onChange={(): void => setFieldValue('terms', !values.terms)}
              />
            </Form.Group>
            <Form.Group controlId="actions" className="actions">
              <LoadingButton
                className="signup-button"
                variant="primary"
                type="submit"
                block
                isLoading={isLoading}
              >
                SUBMIT
              </LoadingButton>
            </Form.Group>
          </Form>
        )}
      </Formik>
    </>
  )
}

export default SignupForm
